import { useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

interface PageTransitionProps {
  children: ReactNode;
}

export default function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.style.transition = "none";
    el.style.opacity = "0";
    el.style.transform = "translateY(16px)";

    let resetTimer: number | undefined;
    const raf = requestAnimationFrame(() => {
      el.style.transition = "opacity 0.4s ease, transform 0.4s ease";
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";

      // Remove the transform after the entrance animation so `position: fixed`
      // children keep anchoring to the viewport while scrolling.
      resetTimer = window.setTimeout(() => {
        el.style.transition = "";
        el.style.transform = "";
      }, 400);
    });

    return () => {
      cancelAnimationFrame(raf);
      if (resetTimer) window.clearTimeout(resetTimer);
    };
  }, [location.pathname]);

  return (
    <div ref={containerRef} style={{ opacity: 0 }}>
      {children}
    </div>
  );
}
